/**
 * Lecture du catalogue consolidé (`json/data_catalog.json`) et des
 * préconisations (`json/recommendations.json`) — contrat toolkit 1.2.
 *
 * Ces deux artefacts sont ingérés comme n'importe quel JSON du zip : le
 * portail les retrouve dans `modules['data_catalog'].json` et
 * `modules['recommendations'].json`. Un audit 1.0 / 1.1 ne les porte pas du
 * tout : `parseDataCatalog` / `parseRecommendations` renvoient alors `null`,
 * et l'onglet correspondant est masqué (jamais une page vide de tirets).
 *
 * Trois règles du contrat sont appliquées ici, à la lettre :
 *
 * 1. `null` n'est pas `0` et n'est pas « non ». Une mesure jamais faite reste
 *    `null` (`reachable`, `network_path_ok`, `score`, `throughput_mbps`) et
 *    s'affiche « non testé », jamais « bloqué » ni « 0 ».
 * 2. Chaque fait porte sa provenance : la machine qui a OBSERVÉ et, pour le
 *    catalogue, celle qui HÉBERGE.
 * 3. Une lacune est un fait : `readiness.gaps[]` et `gaps_in_audit[]` sont
 *    conservés tels quels, jamais filtrés.
 *
 * Les formes intermédiaires (`CatalogSource`, `HardwareOption`, …) ne sont
 * exportées que lorsqu'un autre module les nomme réellement : les onglets
 * consomment le résultat de `parseDataCatalog` / `parseRecommendations` par
 * inférence, sans avoir à importer chaque interface.
 */

// ─── Utilitaires de lecture tolérante ────────────────────────────────────

type Json = Record<string, unknown>;

const isObj = (v: unknown): v is Json => !!v && typeof v === 'object' && !Array.isArray(v);
const arr = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);
const objs = (v: unknown): Json[] => arr(v).filter(isObj);
const str = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null);
const num = (v: unknown): number | null =>
  (typeof v === 'number' && Number.isFinite(v) ? v : null);
const bool = (v: unknown): boolean | null => (typeof v === 'boolean' ? v : null);
const strs = (v: unknown): string[] => arr(v).filter((x): x is string => typeof x === 'string');
const obj = (v: unknown): Json => (isObj(v) ? v : {});

/** `{email: 2, iban: 1}` en ne gardant que les compteurs numériques. */
const counters = (v: unknown): Record<string, number> => {
  const out: Record<string, number> = {};
  for (const [k, n] of Object.entries(obj(v))) {
    const value = num(n);
    if (value != null) out[k] = value;
  }
  return out;
};

// ─── data_catalog.json ───────────────────────────────────────────────────

interface CatalogSource {
  id: string;
  kind: string;
  engine: string | null;
  version: string | null;
  host: string | null;
  port: number | null;
  locator: string | null;
  hosting: string | null;
  environment: string | null;
  /** `null` = jamais contacté (le portail dit « non testé », pas « injoignable »). */
  reachable: boolean | null;
  authMode: string | null;
  discoveryMethod: string | null;
  discoveryEvidence: string | null;
  confidence: number | null;
  /** Machine qui HÉBERGE la source (provenance.host). */
  hostedBy: string | null;
  /** Machine qui a OBSERVÉ la source (provenance.collected_by). */
  observedBy: string | null;
  notes: string[];
}

interface CatalogDataset {
  id: string;
  sourceId: string | null;
  name: string;
  kind: string | null;
  path: string | null;
  formatFamily: string | null;
  sizeBytes: number | null;
  fileCount: number | null;
  lastModified: string | null;
  businessDomain: string | null;
  sensitivityRef: string | null;
  ragPriority: string | null;
}

interface CatalogConnector {
  sourceId: string | null;
  recommended: string | null;
  feasibility: string | null;
  requirements: string[];
  blockers: string[];
  networkPathOk: boolean | null;
  latencyMs: number | null;
  throughputMbps: number | null;
  incrementalStrategy: string | null;
}

interface CatalogPrincipal { name: string; type: string | null; rights: string[] }

interface CatalogAccess {
  id: string;
  target: string;
  targetKind: string | null;
  principals: CatalogPrincipal[];
  publicOrEveryone: boolean | null;
  identityProvider: string | null;
}

interface CatalogSensitivity {
  targetPath: string;
  businessDomain: string | null;
  level: string | null;
  classification: string | null;
  piiTypes: Record<string, number>;
  secretTypes: Record<string, number>;
  fileCount: number | null;
  sizeBytes: number | null;
  /** `name_only` = seuls les noms ont été lus ; `name+content` = profilage opt-in. */
  method: string | null;
}

interface CatalogGap { code: string; severity: string; sourceId: string | null; message: string }

interface CatalogScore {
  name: string;
  /** `null` = dénominateur vide, donc score NON calculable (pas « zéro »). */
  value: number | null;
  considered: number | null;
  satisfied: number | null;
}

interface DataCatalog {
  collectorHost: string | null;
  generatedAt: string | null;
  sources: CatalogSource[];
  datasets: CatalogDataset[];
  connectors: CatalogConnector[];
  access: CatalogAccess[];
  sensitivity: CatalogSensitivity[];
  gaps: CatalogGap[];
  scores: CatalogScore[];
}

function parseSource(s: Json): CatalogSource {
  const locator = obj(s.locator);
  const discovery = obj(s.discovery);
  const provenance = obj(s.provenance);
  return {
    id: str(s.id) ?? '',
    kind: str(s.kind) ?? 'unknown',
    engine: str(s.engine),
    version: str(s.version),
    host: str(locator.host),
    port: num(locator.port),
    locator: str(locator.path) ?? str(locator.url),
    hosting: str(s.hosting),
    environment: str(s.environment),
    reachable: bool(s.reachable),
    authMode: str(obj(s.auth).mode),
    discoveryMethod: str(discovery.method),
    discoveryEvidence: str(discovery.evidence),
    confidence: num(discovery.confidence),
    hostedBy: str(provenance.host),
    observedBy: str(provenance.collected_by),
    notes: strs(s.notes),
  };
}

function parseDataset(d: Json): CatalogDataset {
  const volume = obj(d.volume);
  return {
    id: str(d.id) ?? '',
    sourceId: str(d.source_id),
    name: str(d.name) ?? '—',
    kind: str(d.kind),
    path: str(d.path_or_locator),
    formatFamily: str(obj(d.format).family),
    sizeBytes: num(volume.size_bytes),
    fileCount: num(volume.file_count),
    lastModified: str(obj(d.freshness).last_modified),
    businessDomain: str(d.business_domain),
    sensitivityRef: str(d.sensitivity_ref),
    ragPriority: str(d.rag_priority_hint),
  };
}

function parseConnector(c: Json): CatalogConnector {
  return {
    sourceId: str(c.source_id),
    recommended: str(c.recommended),
    feasibility: str(c.feasibility),
    requirements: strs(c.requirements),
    blockers: strs(c.blockers),
    networkPathOk: bool(c.network_path_ok),
    latencyMs: num(c.latency_ms),
    throughputMbps: num(c.throughput_mbps),
    incrementalStrategy: str(c.incremental_strategy),
  };
}

function parseAccess(a: Json): CatalogAccess {
  return {
    id: str(a.id) ?? '',
    target: str(a.target) ?? '—',
    targetKind: str(a.target_kind),
    principals: objs(a.principals).map(p => ({
      name: str(p.name) ?? '—',
      type: str(p.type),
      rights: strs(p.rights),
    })),
    publicOrEveryone: bool(a.public_or_everyone),
    identityProvider: str(a.identity_provider),
  };
}

function parseSensitivity(e: Json): CatalogSensitivity {
  return {
    targetPath: str(e.target_path) ?? '—',
    businessDomain: str(e.business_domain),
    level: str(e.sensitivity_level),
    classification: str(e.classification),
    piiTypes: counters(e.pii_types),
    secretTypes: counters(e.secret_types),
    fileCount: num(e.file_count),
    sizeBytes: num(e.size_bytes),
    method: str(e.method),
  };
}

/**
 * `json/data_catalog.json` → structure prête à afficher, ou `null` si l'audit
 * ne porte pas de catalogue (contrat 1.0 / 1.1, ou artefact illisible).
 */
export function parseDataCatalog(json: unknown): DataCatalog | null {
  if (!isObj(json)) return null;
  const readiness = obj(json.readiness);
  const basis = obj(readiness.data_scores_basis);
  return {
    collectorHost: str(json.collector_host),
    generatedAt: str(json.generated_at),
    sources: objs(json.sources).map(parseSource),
    datasets: objs(json.datasets).map(parseDataset),
    connectors: objs(json.connectors).map(parseConnector),
    access: objs(json.access).map(parseAccess),
    sensitivity: objs(json.sensitivity).map(parseSensitivity),
    gaps: objs(readiness.gaps).map(g => ({
      code: str(g.code) ?? '—',
      severity: str(g.severity) ?? 'info',
      sourceId: str(g.source_id),
      message: str(g.message) ?? '',
    })),
    scores: Object.entries(obj(readiness.data_scores)).map(([name, value]) => ({
      name,
      value: num(value),
      considered: num(obj(obj(basis)[name]).considered),
      satisfied: num(obj(obj(basis)[name]).satisfied),
    })),
  };
}

// ─── Filtres de l'onglet « Données » ─────────────────────────────────────

export interface SourceFilter { kind: string; engine: string; host: string }

export const NO_FILTER = 'all';

export const EMPTY_SOURCE_FILTER: SourceFilter = { kind: NO_FILTER, engine: NO_FILTER, host: NO_FILTER };

/** Valeurs distinctes proposées par les trois filtres (triées, sans doublon). */
export function sourceFilterOptions(sources: CatalogSource[]): Record<keyof SourceFilter, string[]> {
  const pick = (fn: (s: CatalogSource) => string | null): string[] =>
    [...new Set(sources.map(fn).filter((v): v is string => !!v))].sort();
  return {
    kind: pick(s => s.kind),
    engine: pick(s => s.engine),
    host: pick(s => s.hostedBy ?? s.host),
  };
}

/** Applique les filtres type / moteur / hôte (`all` = pas de filtre). */
export function filterSources(sources: CatalogSource[], f: SourceFilter): CatalogSource[] {
  return sources.filter(s =>
    (f.kind === NO_FILTER || s.kind === f.kind)
    && (f.engine === NO_FILTER || s.engine === f.engine)
    && (f.host === NO_FILTER || (s.hostedBy ?? s.host) === f.host));
}

/** Index `id → source` pour rattacher un dataset ou un connecteur à sa source. */
export function sourcesById(sources: CatalogSource[]): Map<string, CatalogSource> {
  return new Map(sources.map(s => [s.id, s]));
}

/** Étiquette lisible d'une source : moteur + hôte, sinon son locator, sinon son id. */
export function sourceLabel(source?: CatalogSource): string {
  if (!source) return '—';
  const parts = [source.engine, source.host ?? source.hostedBy].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : (source.locator ?? source.id);
}

// ─── recommendations.json ────────────────────────────────────────────────

interface RecoReason { code: string; detail: string; favours: string | null; weight: number | null }
interface RecoAlternative { mode: string; detail: string; conditions: string[] }

export interface HardwareOption {
  tier: string;
  label: string;
  modelClass: string | null;
  quantization: string | null;
  gpuClass: string | null;
  totalVramGb: number | null;
  cpuCores: number | null;
  ramGb: number | null;
  storageGb: number | null;
  storageClass: string | null;
  networkMbps: number | null;
  concurrentUsers: number | null;
  limits: string[];
  recommended: boolean;
}

interface Assumption { code: string; label: string; value: string; unit: string | null }

interface PrioritySource {
  rank: number | null;
  label: string;
  kind: string | null;
  domains: string[];
  sensitivityLevel: string | null;
  documents: number | null;
  bytes: number | null;
  connector: string | null;
  feasibility: string | null;
  blockers: string[];
  score: number | null;
}

interface Prerequisite { code: string; label: string; detail: string; owner: string | null; blocking: boolean }
interface Risk { code: string; label: string; detail: string; severity: string; mitigation: string | null }
interface Workstream {
  id: string; title: string; objective: string; effort: string | null;
  dependsOn: string[]; prerequisites: string[]; deliverable: string | null;
}
interface ScorecardDomain {
  name: string;
  /** `null` = domaine NON MESURÉ (affiché « non mesuré », jamais 0/5). */
  score: number | null;
  ratio: number | null;
  measured: boolean;
  blocking: boolean;
  justification: string | null;
}
interface AuditGap { code: string; detail: string; impact: string | null; severity: string }

export interface Recommendations {
  mode: string | null;
  confidence: string | null;
  reasons: RecoReason[];
  alternatives: RecoAlternative[];
  options: HardwareOption[];
  recommendedTier: string | null;
  assumptions: Assumption[];
  sizingBasis: { tokens: number | null; chunks: number | null; vectors: number | null; indexBytes: number | null; documents: number | null; bytesNet: number | null };
  ingestion: { hoursInitial: number | null; hoursNetwork: number | null; hoursOcr: number | null; throughputMbps: number | null; bottleneck: string | null; bytes: number | null };
  prioritySources: PrioritySource[];
  prerequisites: Prerequisite[];
  risks: Risk[];
  workstreams: Workstream[];
  scorecard: ScorecardDomain[];
  overall: { score: number | null; domainsMeasured: number | null; domainsTotal: number | null; blockingDomains: string[] };
  gaps: AuditGap[];
}

/** Les six domaines de la scorecard, dans l'ordre du schéma. */
export const SCORECARD_DOMAINS = [
  'compute', 'stockage', 'reseau', 'sources_de_donnees', 'acces_securite', 'gouvernance',
] as const;

function parseOption(o: Json, recommendedTier: string | null): HardwareOption {
  const gpu = obj(o.gpu);
  const tier = str(o.tier) ?? '';
  return {
    tier,
    label: str(o.label) ?? tier,
    modelClass: str(o.model_class),
    quantization: str(o.quantization),
    gpuClass: str(gpu.class),
    totalVramGb: num(gpu.total_vram_gb),
    cpuCores: num(o.cpu_cores),
    ramGb: num(o.ram_gb),
    storageGb: num(o.storage_gb),
    storageClass: str(o.storage_class),
    networkMbps: num(o.network_mbps),
    concurrentUsers: num(o.concurrent_users),
    limits: strs(o.limits),
    recommended: tier !== '' && tier === recommendedTier,
  };
}

/**
 * `json/recommendations.json` → structure prête à afficher, ou `null` si
 * l'audit ne porte pas de préconisations (contrat 1.0 / 1.1).
 */
export function parseRecommendations(json: unknown): Recommendations | null {
  if (!isObj(json)) return null;
  const deployment = obj(json.deployment);
  const hardware = obj(json.hardware_recommendation);
  const sizing = obj(hardware.sizing_basis);
  const ingestion = obj(hardware.ingestion);
  const scorecard = obj(json.scorecard);
  const overall = obj(scorecard.overall);
  const recommendedTier = str(hardware.recommended_tier);
  return {
    mode: str(deployment.mode),
    confidence: str(deployment.confidence),
    reasons: objs(deployment.reasons).map(r => ({
      code: str(r.code) ?? '—',
      detail: str(r.detail) ?? '',
      favours: str(r.favours),
      weight: num(r.weight),
    })),
    alternatives: objs(deployment.alternatives).map(a => ({
      mode: str(a.mode) ?? '—',
      detail: str(a.detail) ?? '',
      conditions: strs(a.conditions),
    })),
    options: objs(hardware.options).map(o => parseOption(o, recommendedTier)),
    recommendedTier,
    assumptions: objs(hardware.assumptions).map(a => ({
      code: str(a.code) ?? '—',
      label: str(a.label) ?? '',
      value: a.value == null ? '—' : String(a.value),
      unit: str(a.unit),
    })),
    sizingBasis: {
      tokens: num(sizing.tokens_estimate),
      chunks: num(sizing.chunks_estimate),
      vectors: num(sizing.vectors_estimate),
      indexBytes: num(sizing.vector_index_bytes),
      documents: num(sizing.documents),
      bytesNet: num(sizing.bytes_net),
    },
    ingestion: {
      hoursInitial: num(ingestion.hours_initial),
      hoursNetwork: num(ingestion.hours_network),
      hoursOcr: num(ingestion.hours_ocr),
      throughputMbps: num(ingestion.throughput_mbps),
      bottleneck: str(ingestion.bottleneck),
      bytes: num(ingestion.bytes_to_ingest),
    },
    prioritySources: objs(json.priority_sources).map(ps => ({
      rank: num(ps.rank),
      label: str(ps.label) ?? '—',
      kind: str(ps.kind),
      domains: strs(ps.business_domains),
      sensitivityLevel: str(ps.sensitivity_level),
      documents: num(ps.documents),
      bytes: num(ps.bytes),
      connector: str(ps.connector),
      feasibility: str(ps.feasibility),
      blockers: strs(ps.blockers),
      score: num(ps.score),
    })),
    prerequisites: objs(json.prerequisites).map(pr => ({
      code: str(pr.code) ?? '—',
      label: str(pr.label) ?? '',
      detail: str(pr.detail) ?? '',
      owner: str(pr.owner_hint),
      blocking: pr.blocking === true,
    })),
    risks: objs(json.risks).map(r => ({
      code: str(r.code) ?? '—',
      label: str(r.label) ?? '',
      detail: str(r.detail) ?? '',
      severity: str(r.severity) ?? 'faible',
      mitigation: str(r.mitigation),
    })),
    workstreams: objs(json.workstreams).map(w => ({
      id: str(w.id) ?? '—',
      title: str(w.title) ?? '',
      objective: str(w.objective) ?? '',
      effort: str(w.effort),
      dependsOn: strs(w.depends_on),
      prerequisites: strs(w.prerequisites),
      deliverable: str(w.deliverable),
    })),
    scorecard: SCORECARD_DOMAINS.filter(name => isObj(scorecard[name])).map(name => {
      const d = obj(scorecard[name]);
      return {
        name,
        score: num(d.score),
        ratio: num(d.ratio),
        measured: d.measured === true,
        blocking: d.blocking === true,
        justification: str(d.justification),
      };
    }),
    overall: {
      score: num(overall.score),
      domainsMeasured: num(overall.domains_measured),
      domainsTotal: num(overall.domains_total),
      blockingDomains: strs(overall.blocking_domains),
    },
    gaps: objs(json.gaps_in_audit).map(g => ({
      code: str(g.code) ?? '—',
      detail: str(g.detail) ?? '',
      impact: str(g.impact),
      severity: str(g.severity) ?? 'info',
    })),
  };
}

// ─── Rendu : badges et libellés partagés par les deux onglets ────────────

export type Tone = 'success' | 'warning' | 'danger' | 'info' | 'neutral';

/** `connectors[].feasibility` → couleur du badge (`blocked` = rouge, `ready` = vert). */
export function feasibilityTone(feasibility: string | null): Tone {
  switch (feasibility) {
    case 'ready': return 'success';
    case 'needs_credentials': return 'warning';
    case 'needs_network': return 'warning';
    case 'blocked': return 'danger';
    default: return 'neutral';
  }
}

/** `sensitivity_level` → couleur du badge (du plus sensible au moins sensible). */
export function sensitivityTone(level: string | null): Tone {
  switch (level) {
    case 'restricted': return 'danger';
    case 'confidential': return 'danger';
    case 'internal': return 'warning';
    case 'none': return 'neutral';
    default: return 'neutral';
  }
}

/** `severity` d'une lacune ou d'un risque → couleur du badge. */
export function severityTone(severity: string | null): Tone {
  switch (severity) {
    case 'blocking': return 'danger';
    case 'eleve': return 'danger';
    case 'warning': return 'warning';
    case 'moyen': return 'warning';
    default: return 'info';
  }
}

/** `reachable` : `true` joignable, `false` injoignable, `null` JAMAIS TESTÉ. */
export function reachableTone(reachable: boolean | null): Tone {
  if (reachable === true) return 'success';
  if (reachable === false) return 'danger';
  return 'neutral';
}
